import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Star,
  Users,
  TrendingUp,
  Award,
  BookOpen,
  Brain,
  MapPin,
  Phone,
  Clock,
  Zap,
  Shield,
  Target,
  ChevronRight,
  GraduationCap,
  ArrowRight,
  Atom,
  FlaskConical,
  Calculator,
  Microscope,
} from "lucide-react";
import FAQAccordion from "../components/FAQAccordion";

/* ─── constants ─────────────────────────────────────────────────────── */
const PHONE = "+919918519991";
const WA_MSG = encodeURIComponent(
  "Hello SSS Achievers, I'm looking for Class 11/12 Science coaching (PCM/PCB). Please share batch details. Thank you."
);
const WA_URL = `https://wa.me/919918519991?text=${WA_MSG}`;

const fade = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5 },
  }),
};

/* ─── data ──────────────────────────────────────────────────────────── */
const STATS = [
  { value: "2", label: "Streams: PCM & PCB", icon: BookOpen },
  { value: "95%", label: "Success Rate", icon: TrendingUp },
  { value: "15+", label: "Expert Educators", icon: GraduationCap },
  { value: "340+", label: "Students Enrolled", icon: Users },
];

const STREAMS = [
  {
    name: "PCM",
    full: "Physics, Chemistry, Mathematics",
    icon: Calculator,
    color: "bg-blue-50 border-blue-200 text-blue-700",
    desc: "Perfect foundation for Engineering, JEE Main/Advanced and other technical entrance exams — alongside strong CBSE board preparation for Class 11 & 12.",
  },
  {
    name: "PCB",
    full: "Physics, Chemistry, Biology",
    icon: Microscope,
    color: "bg-emerald-50 border-emerald-200 text-emerald-700",
    desc: "Ideal foundation for Medical, NEET and other life-science entrance exams — alongside strong CBSE board preparation for Class 11 & 12.",
  },
];

const SCIENCE_SUBJECTS = [
  { subject: "Physics", icon: Atom, streams: "PCM & PCB", desc: "Mechanics, Electrostatics, Current Electricity, Optics and Modern Physics — concept clarity with numerical practice for boards and competitive exams." },
  { subject: "Chemistry", icon: FlaskConical, streams: "PCM & PCB", desc: "Physical, Organic and Inorganic Chemistry — reaction mechanisms, numericals and NCERT-based theory for board and entrance exams." },
  { subject: "Mathematics", icon: Calculator, streams: "PCM", desc: "Calculus, Algebra, Coordinate Geometry, Vectors and Probability — strong problem-solving practice for boards and engineering entrance foundation." },
  { subject: "Biology", icon: Microscope, streams: "PCB", desc: "Botany and Zoology — diagrams, NCERT-based theory and MCQ practice for boards and medical entrance foundation." },
];

const SCIENCE_FEATURES = [
  { icon: Brain, title: "Strong NCERT Foundation", desc: "Every concept is built on a rock-solid NCERT base — the same foundation CBSE boards and entrance exams are based on." },
  { icon: Target, title: "Board + Competitive Focus", desc: "An integrated approach that prepares students for Class 11/12 boards while building a head-start for JEE/NEET-style competitive exams." },
  { icon: Zap, title: "Numerical & Practical Practice", desc: "Regular numerical problem-solving and practical-based questions across Physics, Chemistry, Maths and Biology." },
  { icon: TrendingUp, title: "Weekly Tests & Analytics", desc: "Chapter-wise weekly tests with performance analytics help identify and fix weak areas early." },
  { icon: Users, title: "Small Batch Sizes", desc: "Science subjects need individual attention — our small, focused batches ensure every doubt gets answered." },
  { icon: Shield, title: "Experienced Subject Specialists", desc: "Dedicated specialist faculty for Physics, Chemistry, Mathematics and Biology — not generalist teachers." },
  { icon: Award, title: "Doubt-Clearing Sessions", desc: "Regular dedicated sessions to clear conceptual and numerical doubts before they pile up." },
  { icon: CheckCircle2, title: "Structured Revision Plans", desc: "Topic-wise revision schedules and formula sheets keep students exam-ready throughout the year." },
];

const COMPARISON = [
  { feature: "Stream Options", sss: "PCM & PCB both offered", others: "Limited stream options" },
  { feature: "Faculty", sss: "Subject specialists per topic", others: "Mixed/generalist teachers" },
  { feature: "Board + Competitive Prep", sss: "Integrated approach", others: "Board-only or coaching-only" },
  { feature: "Batch Size", sss: "Small Batch (focused)", others: "50+ students (crowded)" },
  { feature: "Numerical Practice", sss: "Regular structured practice", others: "Occasional, unstructured" },
  { feature: "Doubt Clearing", sss: "Dedicated sessions", others: "Limited or extra fee" },
  { feature: "Demo Class", sss: "Free before enrollment", others: "Usually not offered" },
];

const TESTIMONIALS = [
  { name: "Aditya Mishra", cls: "Class 12 PCM, 2025", rating: 5, text: "SSS Achievers gave me a strong base in Physics and Maths for both boards and engineering entrance prep. The numerical practice sessions were extremely helpful." },
  { name: "Sneha Pathak", cls: "Class 11 PCB, 2025", rating: 5, text: "Biology and Chemistry felt overwhelming at first, but the specialist faculty here broke everything down clearly. Best science coaching for Class 11 in Ghazipur." },
  { name: "Rohit Chauhan", cls: "Class 12 PCM, 2025", rating: 5, text: "The weekly tests and analytics helped me track exactly where I was weak. My Physics and Chemistry scores improved a lot before boards." },
  { name: "Anjali Rai (Parent)", cls: "Parent of Class 11 PCB Student", rating: 5, text: "Small batch size means my daughter gets personal attention in every science subject. Truly the best coaching for 11th and 12th science near us." },
];

/* ─── component ─────────────────────────────────────────────────────── */
export default function Class1112ScienceCoachingGhazipur() {
  return (
    <>
      <Helmet>
        <title>Best Coaching for Class 11 & 12 Science (PCM/PCB) | SSS Achievers Ghazipur</title>
        <meta
          name="description"
          content="Best coaching for Class 11 & 12 Science in Ghazipur — PCM (Physics, Chemistry, Maths) & PCB (Physics, Chemistry, Biology). Expert faculty, small batches, weekly tests, 95% success rate. Free demo class."
        />
        <meta
          name="keywords"
          content="best coaching classes for 11th and 12th science near me, class 11 science coaching Ghazipur, class 12 science coaching Ghazipur, PCM coaching Ghazipur, PCB coaching Ghazipur, science coaching near me, 11th 12th science tuition Ghazipur, SSS Achievers science coaching"
        />
        <link rel="canonical" href="https://www.sssachievers.com/class-11-12-science-coaching-ghazipur" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Best Coaching for Class 11 & 12 Science | SSS Achievers Ghazipur" />
        <meta property="og:description" content="PCM & PCB coaching for Class 11 & 12 in Ghazipur — expert faculty, small batches, weekly tests, 95% success rate. Free demo class." />
        <meta property="og:url" content="https://www.sssachievers.com/class-11-12-science-coaching-ghazipur" />
        <meta property="og:image" content="https://www.sssachievers.com/logo512.png" />
        <meta property="og:locale" content="en_IN" />
        <meta property="og:site_name" content="SSS Achievers" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Best Coaching for Class 11 & 12 Science | SSS Achievers" />
        <meta name="twitter:description" content="PCM & PCB coaching for Class 11 & 12 near you in Ghazipur. Expert faculty, small batches, free demo class." />
        <meta name="twitter:image" content="https://www.sssachievers.com/logo512.png" />
        <meta name="geo.region" content="IN-UP" />
        <meta name="geo.placename" content="Ghazipur, Uttar Pradesh" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "EducationalOrganization",
                "@id": "https://www.sssachievers.com/#organization",
                "name": "SSS Achievers",
                "alternateName": ["Class 11 Science Coaching Ghazipur", "Class 12 Science Coaching Ghazipur", "PCM PCB Coaching Ghazipur"],
                "description": "SSS Achievers offers the best coaching for Class 11 and Class 12 Science (PCM & PCB) in Ghazipur, Uttar Pradesh with expert faculty and a 95% success rate.",
                "url": "https://www.sssachievers.com",
                "logo": "https://www.sssachievers.com/logo512.png",
                "telephone": "+919918519991",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "96/B, Badi Bagh",
                  "addressLocality": "Ghazipur",
                  "addressRegion": "Uttar Pradesh",
                  "postalCode": "233001",
                  "addressCountry": "IN"
                },
                "geo": {
                  "@type": "GeoCoordinates",
                  "latitude": 25.5825,
                  "longitude": 83.5805
                },
                "aggregateRating": {
                  "@type": "AggregateRating",
                  "ratingValue": "4.9",
                  "reviewCount": "340",
                  "bestRating": "5"
                },
                "hasOfferCatalog": {
                  "@type": "OfferCatalog",
                  "name": "Class 11 & 12 Science Courses",
                  "itemListElement": [
                    { "@type": "Course", "name": "Class 11 Foundation (PCM/PCB)", "url": "https://www.sssachievers.com/courses/class-11-foundation" },
                    { "@type": "Course", "name": "Class 12 Board Preparation (PCM/PCB)", "url": "https://www.sssachievers.com/courses/class-12-board-preparation" }
                  ]
                }
              },
              {
                "@type": "FAQPage",
                "mainEntity": [
                  {
                    "@type": "Question",
                    "name": "Which is the best coaching for Class 11 and 12 Science in Ghazipur?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "SSS Achievers is widely regarded as the best coaching for Class 11 and 12 Science in Ghazipur, offering both PCM (Physics, Chemistry, Maths) and PCB (Physics, Chemistry, Biology) streams with expert subject specialists, small batches and a 95% success rate."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Does SSS Achievers offer both PCM and PCB streams for Class 11 and 12?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes — SSS Achievers offers coaching for both PCM (Physics, Chemistry, Mathematics) and PCB (Physics, Chemistry, Biology) streams for Class 11 and Class 12 students."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Does Class 11/12 Science coaching at SSS Achievers help with JEE or NEET preparation?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes — our Class 11/12 Science coaching builds a strong NCERT foundation and includes numerical/conceptual practice that supports both CBSE board exams and a head-start for JEE (PCM) or NEET (PCB) style competitive exams."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "What is the batch size for Class 11 and 12 Science at SSS Achievers?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Class 11 and 12 Science batches at SSS Achievers are kept intentionally small, ensuring every student receives individual attention and regular doubt-clearing across Physics, Chemistry, Mathematics and Biology."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Is there a free demo class for Class 11/12 Science at SSS Achievers Ghazipur?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes — SSS Achievers offers a completely free demo class for Class 11/12 Science (PCM/PCB) so students and parents can experience the teaching quality before enrolling."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "What is the fee for Class 11/12 Science coaching at SSS Achievers?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Fees for Class 11/12 Science coaching (PCM/PCB) are available on request. Contact us at +91 99185 19991 or WhatsApp for current fee details and installment options."
                    }
                  }
                ]
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.sssachievers.com/" },
                  { "@type": "ListItem", "position": 2, "name": "Class 11 & 12 Science Coaching", "item": "https://www.sssachievers.com/class-11-12-science-coaching-ghazipur" }
                ]
              }
            ]
          }
        `}</script>
      </Helmet>

      <main className="bg-white text-slate-900">
        {/* ══ HERO ══ */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#0097b2]/8 via-white to-[#fcbf0a]/8 pt-10 pb-14 md:pt-20 md:pb-24">
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -top-32 -right-32 w-72 h-72 bg-[#0097b2]/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-32 -left-32 w-72 h-72 bg-[#fcbf0a]/10 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav aria-label="breadcrumb" className="mb-6 flex items-center gap-2 text-sm text-slate-500">
              <Link to="/" className="hover:text-[#0097b2] transition">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/courses" className="hover:text-[#0097b2] transition">Courses</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-[#0097b2] font-medium">Class 11 & 12 Science Coaching</span>
            </nav>

            <div className="grid md:grid-cols-2 gap-10 items-center">
              <motion.div initial="hidden" animate="visible" variants={fade} className="space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#fcbf0a]/20 border border-[#fcbf0a]/40">
                  <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                  <span className="text-sm font-semibold text-amber-700">Ghazipur's Best Science Coaching — PCM & PCB</span>
                </div>

                <h1 className="text-4xl md:text-5xl font-bold leading-tight text-slate-900">
                  Best Coaching for <span className="text-[#0097b2]">Class 11 & 12 Science</span> Near You
                </h1>

                <p className="text-base md:text-lg text-slate-600 max-w-xl leading-relaxed">
                  SSS Achievers offers expert coaching for Class 11 and 12 Science students — both PCM (Physics, Chemistry, Maths) and PCB (Physics, Chemistry, Biology) — combining strong board preparation with a head-start for competitive exams.
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#0097b2] text-white font-semibold rounded hover:bg-[#007a91] shadow-md hover:shadow-lg transition">
                    Book Free Demo Class <ArrowRight className="w-4 h-4" />
                  </a>
                  <a href={`tel:${PHONE}`} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border-2 border-[#0097b2] text-[#0097b2] font-semibold rounded hover:bg-[#0097b2]/5 transition">
                    <Phone className="w-4 h-4" /> Call: +91 99185 19991
                  </a>
                </div>

                <div className="flex flex-wrap gap-4 pt-2">
                  {["PCM & PCB Both Offered", "Board + Competitive Focus", "Small Batches", "95% Success Rate"].map((t) => (
                    <span key={t} className="inline-flex items-center gap-1.5 text-sm text-slate-600">
                      <CheckCircle2 className="w-4 h-4 text-[#0097b2]" /> {t}
                    </span>
                  ))}
                </div>
              </motion.div>

              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="grid grid-cols-2 gap-4">
                {STATS.map(({ value, label, icon: Icon }) => (
                  <div key={label} className="bg-white rounded-2xl p-6 shadow-md border border-gray-100 flex flex-col items-center text-center hover:shadow-lg transition">
                    <div className="w-12 h-12 rounded-xl bg-[#0097b2]/10 flex items-center justify-center mb-3">
                      <Icon className="w-6 h-6 text-[#0097b2]" />
                    </div>
                    <span className="text-3xl font-bold text-[#0097b2]">{value}</span>
                    <span className="text-sm text-slate-500 mt-1 font-medium">{label}</span>
                  </div>
                ))}
                <div className="col-span-2 bg-gradient-to-r from-[#0097b2] to-[#006f85] rounded-2xl p-5 text-white flex items-center gap-4">
                  <div>
                    <div className="flex gap-1 mb-1">
                      {[1, 2, 3, 4, 5].map((s) => <Star key={s} className="w-5 h-5 fill-[#fcbf0a] text-[#fcbf0a]" />)}
                    </div>
                    <p className="font-bold text-lg">4.9 / 5.0 Rating</p>
                    <p className="text-blue-100 text-sm">Trusted by 340+ students</p>
                  </div>
                  <div className="ml-auto text-right">
                    <p className="text-blue-100 text-xs uppercase tracking-wider font-semibold">Streams</p>
                    <p className="text-2xl font-bold">PCM / PCB</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ══ STREAMS ══ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                Choose Your <span className="text-[#0097b2]">Science Stream</span>
              </motion.h2>
              <p className="mt-3 text-slate-600">Both PCM and PCB streams available for Class 11 & 12</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {STREAMS.map(({ name, full, icon: Icon, color, desc }, i) => (
                <motion.div key={name} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }} custom={i} variants={fade}
                  className={`rounded-2xl border-2 ${color} bg-white p-8 hover:shadow-lg transition-all`}>
                  <div className="w-14 h-14 rounded-xl bg-white shadow-sm flex items-center justify-center mb-4">
                    <Icon className="w-7 h-7" />
                  </div>
                  <h3 className="font-bold text-2xl mb-1">{name}</h3>
                  <p className="text-sm font-semibold mb-3">{full}</p>
                  <p className="text-slate-600 text-sm leading-relaxed">{desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ SUBJECTS ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-[#0097b2]/5 to-[#fcbf0a]/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                Subjects Taught by <span className="text-[#0097b2]">Specialist Faculty</span>
              </motion.h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {SCIENCE_SUBJECTS.map(({ subject, icon: Icon, streams, desc }, i) => (
                <motion.div key={subject} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }} custom={i} variants={fade}
                  className="rounded-2xl border-2 border-gray-100 hover:border-[#0097b2]/40 bg-white p-6 hover:shadow-lg transition-all">
                  <div className="w-12 h-12 rounded-xl bg-[#0097b2]/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-[#0097b2]" />
                  </div>
                  <h3 className="font-bold text-slate-800 text-lg mb-1">{subject}</h3>
                  <p className="text-xs font-semibold text-[#0097b2] mb-2">{streams}</p>
                  <p className="text-slate-500 text-sm leading-relaxed">{desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ FEATURES ══ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                Why SSS Achievers is the <span className="text-[#0097b2]">Best Choice</span> for Science
              </motion.h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {SCIENCE_FEATURES.map(({ icon: Icon, title, desc }, i) => (
                <motion.div key={title} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }} custom={i} variants={fade}
                  className="group p-6 rounded-2xl border-2 border-gray-100 hover:border-[#0097b2]/40 hover:shadow-lg transition-all duration-300 bg-white">
                  <div className="w-12 h-12 rounded-xl bg-[#0097b2]/10 flex items-center justify-center mb-4 group-hover:bg-[#0097b2]/20 transition-colors">
                    <Icon className="w-6 h-6 text-[#0097b2]" />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-2 text-base">{title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ COMPARISON ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-[#0097b2]/5 to-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                SSS Achievers vs. Other Science Coaching in Ghazipur
              </motion.h2>
            </div>
            <div className="overflow-hidden rounded-2xl border-2 border-gray-100 shadow-sm">
              <div className="grid grid-cols-3 bg-[#0097b2] text-white text-sm font-bold">
                <div className="px-5 py-4">Feature</div>
                <div className="px-5 py-4 text-center border-l border-white/20">SSS Achievers ✓</div>
                <div className="px-5 py-4 text-center border-l border-white/20 text-blue-100">Others</div>
              </div>
              {COMPARISON.map(({ feature, sss, others }, i) => (
                <div key={feature} className={`grid grid-cols-3 text-sm border-b border-gray-100 ${i % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
                  <div className="px-5 py-4 font-semibold text-slate-700">{feature}</div>
                  <div className="px-5 py-4 text-center border-l border-gray-100">
                    <div className="flex items-center justify-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#0097b2] flex-shrink-0" />
                      <span className="text-slate-700 font-medium">{sss}</span>
                    </div>
                  </div>
                  <div className="px-5 py-4 text-center border-l border-gray-100 text-slate-400 text-xs">{others}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ TESTIMONIALS ══ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                What Science Students Say About <span className="text-[#0097b2]">SSS Achievers</span>
              </motion.h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {TESTIMONIALS.map(({ name, cls, rating, text }, i) => (
                <motion.div key={name} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }} custom={i} variants={fade}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition flex flex-col">
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: rating }).map((_, s) => <Star key={s} className="w-4 h-4 fill-[#fcbf0a] text-[#fcbf0a]" />)}
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed flex-1 italic">"{text}"</p>
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="font-semibold text-slate-800 text-sm">{name}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{cls}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ LOCATION / ENROLL ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-[#0097b2]/5 to-[#fcbf0a]/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="space-y-5">
                <h2 className="text-3xl md:text-4xl font-bold">
                  Enroll for <span className="text-[#0097b2]">Class 11 & 12 Science Coaching</span> Today
                </h2>
                <p className="text-slate-600">Seats are limited per batch for PCM and PCB. Visit SSS Achievers or book via WhatsApp.</p>
                {[
                  { icon: MapPin, text: "96/B, Badi Bagh, Opp. Raj Nursing Home, Ghazipur, UP – 233001" },
                  { icon: Phone, text: "+91 99185 19991", href: `tel:${PHONE}` },
                  { icon: Clock, text: "Mon–Sat: 9:00 AM – 7:00 PM" },
                ].map(({ icon: Icon, text, href }) => (
                  <div key={text} className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#0097b2]/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-[#0097b2]" />
                    </div>
                    {href ? <a href={href} className="text-sm text-[#0097b2] font-semibold hover:underline mt-2">{text}</a> : <p className="text-sm text-slate-600 mt-2">{text}</p>}
                  </div>
                ))}
                <div className="flex flex-wrap gap-3 pt-2">
                  <Link to="/courses/class-11-foundation" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="inline-flex items-center gap-2 px-5 py-3 bg-[#0097b2] text-white font-semibold rounded hover:bg-[#007a91] transition shadow-md text-sm">
                    Class 11 Foundation <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/courses/class-12-board-preparation" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="inline-flex items-center gap-2 px-5 py-3 border-2 border-[#0097b2] text-[#0097b2] font-semibold rounded hover:bg-[#0097b2]/5 transition text-sm">
                    Class 12 Board Prep
                  </Link>
                </div>
              </motion.div>

              <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="rounded-3xl overflow-hidden border-2 border-gray-100 shadow-lg">
                <div className="bg-[#0097b2] px-6 py-4 flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-white" />
                  <span className="text-white font-semibold">SSS Achievers — Ghazipur</span>
                </div>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3598.750962143519!2d83.56063587575478!3d25.57995091609078!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2bb0bb23b08ac59%3A0x4c04724e0b4e6039!2sSSS%20ACHIEVERS%20Coaching%20Centre%20Ghazipur!5e0!3m2!1sen!2sin!4v1775367597808!5m2!1sen!2sin"
                  width="100%"
                  height="280"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="SSS Achievers Class 11 12 Science Coaching Ghazipur Location"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* ══ FAQ ══ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">Frequently Asked Questions</motion.h2>
              <p className="mt-3 text-slate-500 text-sm">About Class 11 & 12 Science coaching at SSS Achievers</p>
            </div>
            <FAQAccordion
              faqs={[
                { q: "Which is the best coaching for Class 11 and 12 Science in Ghazipur?", a: "SSS Achievers is widely regarded as the best coaching for Class 11 and 12 Science in Ghazipur, offering both PCM (Physics, Chemistry, Maths) and PCB (Physics, Chemistry, Biology) streams with expert subject specialists, small batches and a 95% success rate." },
                { q: "Does SSS Achievers offer both PCM and PCB streams?", a: "Yes — SSS Achievers offers coaching for both PCM (Physics, Chemistry, Mathematics) and PCB (Physics, Chemistry, Biology) streams for Class 11 and Class 12 students." },
                { q: "Does this coaching help with JEE or NEET preparation?", a: "Yes — our Class 11/12 Science coaching builds a strong NCERT foundation and includes numerical/conceptual practice that supports both CBSE board exams and a head-start for JEE (PCM) or NEET (PCB) style competitive exams." },
                { q: "What is the batch size for Class 11 and 12 Science?", a: "Class 11 and 12 Science batches at SSS Achievers are kept intentionally small, ensuring every student gets individual attention and regular doubt-clearing across Physics, Chemistry, Mathematics and Biology." },
                { q: "Is there a free demo class?", a: "Yes — SSS Achievers offers a completely free demo class for Class 11/12 Science (PCM/PCB) so students and parents can experience the teaching quality before enrolling." },
                { q: "What is the fee for Class 11/12 Science coaching?", a: "Fees for Class 11/12 Science coaching (PCM/PCB) are available on request. Contact us at +91 99185 19991 or WhatsApp for current details and installment options." },
              ]}
            />
          </div>
        </section>

        {/* ══ FINAL CTA ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-r from-[#0097b2] to-[#006f85] relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-16 -right-16 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
            <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-[#fcbf0a]/10 rounded-full blur-3xl" />
          </div>
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20">
                <Star className="w-4 h-4 text-[#fcbf0a] fill-[#fcbf0a]" />
                <span className="text-white text-sm font-semibold">Best Coaching for 11th & 12th Science Near You</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white">Build Your Science Foundation with SSS Achievers</h2>
              <p className="text-blue-100 text-base md:text-lg max-w-2xl mx-auto">
                Limited seats available for PCM and PCB batches. Book your free demo class today.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#fcbf0a] text-gray-900 font-bold rounded hover:bg-[#e6ac00] transition shadow-lg text-base">
                  Book Free Demo Class <ArrowRight className="w-5 h-5" />
                </a>
                <a href={`tel:${PHONE}`} className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 border-2 border-white/30 text-white font-bold rounded hover:bg-white/20 transition text-base">
                  <Phone className="w-5 h-5" /> +91 99185 19991
                </a>
              </div>
              <p className="text-blue-200 text-xs mt-4">📍 96/B, Badi Bagh, Opp. Raj Nursing Home, Ghazipur, UP – 233001</p>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
}
